import { useNavigate } from '@tanstack/react-router';
import { Check, FileStack } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { GroupType } from '@/components/bundle/bundleTypes';
import { CreateBundleDrawer } from '@/components/bundle/CreateBundleDrawer';
import { Spinner } from '@/components/shared';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import type { BundleFileCreate, BundleGroupCreate, BundleGroupResponse, BundleResponse } from '@/hooks/useBundle';
import { type BundleListItem, useBundles } from '@/hooks/useBundles';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { useRegionBundlePaths } from '@/lib/bundleRoutes';

type AddToBundleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDocuments: { id: string; name?: string }[];
  matterGuid: string;
};

type DialogState = 'select' | 'success';

export const AddToBundleDialog = ({ open, onOpenChange, ...rest }: AddToBundleDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AddToBundleDialogContent onOpenChange={onOpenChange} {...rest} />
    </Dialog>
  );
};

const AddToBundleDialogContent = ({
  onOpenChange,
  selectedDocuments,
  matterGuid,
}: Omit<AddToBundleDialogProps, 'open'>) => {
  const navigate = useNavigate();
  const bundlePaths = useRegionBundlePaths();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const { bundles, isLoading: bundlesLoading, mutate } = useBundles(matterGuid);
  const [dialogState, setDialogState] = useState<DialogState>('select');
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [resultBundleId, setResultBundleId] = useState<string | null>(null);
  const [resultBundleTitle, setResultBundleTitle] = useState('');

  const fileCount = selectedDocuments.length;

  const buildFilesForGroup = (): BundleFileCreate[] =>
    selectedDocuments.map((doc, index) => ({
      bundle_file_id: null,
      file_reference_guid: doc.id,
      order_index: index,
    }));

  const handleSuccess = (bundleId: string, bundleTitle: string) => {
    mutate();
    setResultBundleId(bundleId);
    setResultBundleTitle(bundleTitle);
    setDialogState('success');
  };

  const handleGoToBundle = () => {
    if (resultBundleId) {
      onOpenChange(false);
      navigate({
        to: bundlePaths.detail,
        params: { slug: matterGuid, bundleId: resultBundleId },
      });
    }
  };

  if (dialogState === 'success') {
    return (
      <DialogContent className="sm:max-w-lg">
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">
              {fileCount} file{fileCount > 1 ? 's' : ''} added to &quot;{resultBundleTitle}&quot;
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} text="Done" />
          <Button onClick={handleGoToBundle} text={ui_labels.bundle_go_to} />
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileStack className="h-5 w-5" />
            Add {fileCount} file{fileCount > 1 ? 's' : ''} to {ui_labels.bundle_singular.toLowerCase()}
          </DialogTitle>
        </DialogHeader>

        <div className="h-80">
          <ExistingBundleTab
            bundles={bundles}
            bundlesLoading={bundlesLoading}
            matterGuid={matterGuid}
            fileCount={fileCount}
            buildFilesForGroup={buildFilesForGroup}
            onSuccess={handleSuccess}
            onCancel={() => onOpenChange(false)}
            onCreateNew={() => {
              onOpenChange(false);
              setIsCreateDrawerOpen(true);
            }}
          />
        </div>
      </DialogContent>

      <CreateBundleDrawer
        open={isCreateDrawerOpen}
        onOpenChange={setIsCreateDrawerOpen}
        matterGuid={matterGuid}
        selectedFiles={selectedDocuments.map(doc => ({
          id: doc.id,
          name: doc.name ?? doc.id,
        }))}
        onSuccess={(bundleId) => {
          mutate();
          navigate({
            to: bundlePaths.detail,
            params: { slug: matterGuid, bundleId },
          });
        }}
      />
    </>
  );
};

const ExistingBundleTab = ({
  bundles,
  bundlesLoading,
  matterGuid,
  fileCount,
  buildFilesForGroup,
  onSuccess,
  onCancel,
  onCreateNew,
}: {
  bundles: BundleListItem[];
  bundlesLoading: boolean;
  matterGuid: string;
  fileCount: number;
  buildFilesForGroup: () => BundleFileCreate[];
  onSuccess: (bundleId: string, bundleTitle: string) => void;
  onCancel: () => void;
  onCreateNew: () => void;
}) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [search, setSearch] = useState('');
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredBundles = bundles
    .filter(b => b.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.title.localeCompare(b.title));

  const handleSubmit = async () => {
    if (!selectedBundleId) return;
    setIsSubmitting(true);

    try {
      const response = await backendClient.get<BundleResponse>(`/bundle/matter/${matterGuid}/${selectedBundleId}`);
      const bundle = response.data;

      const groups: BundleGroupCreate[] = bundle.groups.map(mapGroupForUpdate);

      const lastGroup = groups.length > 0
        ? groups.reduce((a, b) => a.order_index > b.order_index ? a : b)
        : null;

      if (lastGroup?.group_type === GroupType.ROOT) {
        const maxOrder = lastGroup.files.length > 0
          ? Math.max(...lastGroup.files.map(f => f.order_index)) + 1
          : 0;
        const newFiles = buildFilesForGroup().map((f, i) => ({ ...f, order_index: maxOrder + i }));
        lastGroup.files = [...lastGroup.files, ...newFiles];
      } else {
        const maxGroupOrder = lastGroup ? lastGroup.order_index + 1 : 0;
        groups.push({
          bundle_group_id: null,
          title: 'Root',
          order_index: maxGroupOrder,
          group_type: GroupType.ROOT,
          files: buildFilesForGroup(),
          subgroups: [],
        });
      }

      await backendClient.put(`/bundle/matter/${matterGuid}/${selectedBundleId}`, {
        title: bundle.title,
        description: bundle.description,
        metadata: bundle.metadata,
        groups,
      });

      const selectedBundle = bundles.find(b => b.bundle_id === selectedBundleId);
      onSuccess(selectedBundleId, selectedBundle?.title ?? ui_labels.bundle_singular);
    } catch (err: any) {
      toast.error(apiErrorMessage(err) || ui_labels.bundle_add_failed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <Input
          placeholder={ui_labels.bundle_search_placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="min-h-0 flex-1 overflow-y-auto space-y-1">
          {bundlesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : filteredBundles.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {bundles.length === 0 ? ui_labels.bundle_none_yet : ui_labels.bundle_none_match}
            </p>
          ) : (
            filteredBundles.map(bundle => (
              <button
                key={bundle.bundle_id}
                onClick={() => setSelectedBundleId(bundle.bundle_id)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                  selectedBundleId === bundle.bundle_id
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:bg-muted'
                }`}
                type='button'
              >
                <div className="font-medium">{bundle.title}</div>
                <div className="text-xs text-muted-foreground">{bundle.file_count} file{bundle.file_count !== 1 ? 's' : ''}</div>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="mt-4 flex w-full items-center justify-between">
        <Button variant="ghost" onClick={onCancel} text="Cancel" />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCreateNew} text={ui_labels.bundle_create_new} />
          <Button
            onClick={handleSubmit}
            disabled={!selectedBundleId || isSubmitting}
            loading={isSubmitting}
            text={`Add ${fileCount} file${fileCount > 1 ? 's' : ''}`}
          />
        </div>
      </div>
    </div>
  );
};

/** Recursively map a BundleGroupResponse to BundleGroupCreate for the update payload */
function mapGroupForUpdate(group: BundleGroupResponse): BundleGroupCreate {
  return {
    bundle_group_id: group.bundle_group_id,
    title: group.title,
    order_index: group.order_index,
    group_type: group.group_type,
    files: group.files.map(f => ({
      bundle_file_id: f.bundle_file_id,
      file_reference_guid: f.file_reference_guid,
      order_index: f.order_index,
      custom_name: f.custom_name,
      page_range: f.page_range,
    })),
    subgroups: group.subgroups.map(mapGroupForUpdate),
  };
}
